import { Icon } from "@superwall/legacy-ui"
import { SModal, SDivider, SFlex, SInput, STooltip, SButton } from "@superwall/legacy-ui"
import useClickedOutsideRef from "app/core/hooks/useClickedOutsideOfRef"
import { useElementContext } from "app/paywallBuilderV2/contexts/ElementProvider"
import { useTemplateJsonContext } from "app/paywallBuilderV2/contexts/JsonProvider"
import {
  addChildToElement,
  addSibling,
  createComponent,
  deleteElement,
  duplicateElement,
  flattenArray,
  updateJsonByElement,
} from "app/paywallBuilderV2/contexts/JsonProvider/utils"
import { useSpecificPaywallContext } from "app/paywalls/components/forms/editorV3/contexts/PaywallContext"
import { TElement } from "app/schema/paywallTemplate/element/element"
import _ from "lodash"
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useField, useFormState } from "react-final-form"
import tw from "tailwind-styled-components"
import { elementsWithChildren } from "../ElementDetails/utils"
import { ESelectComponentModalEvent, TOpenSelectComponentModalEvent } from "./SelectComponentModal"
import { updateNecessarySubstitutions } from "./util"

const Container = tw.div`
  group
  relative
  py-2
  pl-4
`

const DraggedEdgeIndicator = tw.div`
  absolute
  left-0
  z-[-1]
  bg-accent-light
  w-full
  h-[1px]
`

const Name = tw.div`
  relative
  flex
  items-center
  justify-between
  grow
  cursor-pointer
  box-border
  rounded-md
  border
  border-accent-light
  border-opacity-10
  p-[8px]
  font-normal

  group-hover:border-accent-light
  group-hover:border-opacity-50
  group-hover:border-2
  group-hover:p-[7px]
`

const Badge = tw.div`
  rounded-md
  p-2
  bg-accent-light/10
  text-xs
`

const SelectedElementStyle = `
  !border-accent-light
  !border-opacity-80
  border-2
  p-[7px]
`

const OpenCloseIconContainer = `
  absolute
  top-[50%]
  left-[-24px]
  translate-y-[-50%]
  opacity-50
  bg-grayscale-darkest
  p-1

  group-hover:block

  hover:opacity-100
`

const AddSiblingIcon = tw.div`
  hidden
  absolute
  left-[50%]
  translate-x-[-50%]
  cursor-pointer

  group-hover:block
`

const SideIconContainer = tw.div`
  cursor-pointer
`

export const componentOptions = [
  { value: "badge", label: "badge" },
  { value: "bullet", label: "bullet" },
  { value: "button", label: "button" },
  { value: "buttonBase", label: "buttonBase" },
  // We don't seem to be using this. Delete if we don't need it.
  // { value: "caption", label: "caption" },
  { value: "card", label: "card" },
  { value: "container", label: "container" },
  { value: "dividingHeader", label: "dividing header" },
  { value: "div", label: "div" },
  { value: "dropdown", label: "dropdown" },
  { value: "heading", label: "heading" },
  { value: "html", label: "html" },
  { value: "image", label: "image" },
  { value: "imageBase", label: "imageBase" },
  { value: "list", label: "list" },
  { value: "lottie", label: "lottie" },
  { value: "marquee", label: "marquee" },
  // { value: "marqueeItem", label: "marquee item" },
  { value: "paragraph", label: "paragraph" },
  { value: "rating", label: "rating" },
  { value: "review", label: "review" },
  { value: "section", label: "section" },
  { value: "subtitle", label: "subtitle" },
  { value: "table", label: "table" },
  { value: "timeline", label: "timeline" },
  { value: "timelineItem", label: "timeline item" },
  { value: "title", label: "title" },
  { value: "video", label: "video" },
]

export type TMarqueeItemCount = "6" | "7" | "8" | "9" | "10"

type TBadge = {
  text: "string"
  tooltip?: string
}

export enum EElementEvent {
  CLICK_NAVIGATOR_ELEMENT = "CLICK_NAVIGATOR_ELEMENT",
  CLICK_PREVIEW_ELEMENT = "CLICK_PREVIEW_ELEMENT",
}

export type TClickNavigatorEvent = CustomEvent<{
  key: string
}>

const Element = ({
  element,
  level = 0,
  toggleElementDetails,
  draggedOverElementId,
  setDraggedOverElementId,
  draggedOverElementPosition,
  setDraggedOverElementPosition,
  draggedDescendantIds,
  setDraggedDescendantIds,
  dragMode,
  setDragMode,
  draggedElement,
  setDraggedElement,
  isElementInteractionDisabled = false,
  badge,
  contentType,
}: {
  element: TElement
  level?: number
  toggleElementDetails: Dispatch<SetStateAction<boolean>>
  draggedOverElementId: string | null
  setDraggedOverElementId: Dispatch<SetStateAction<string | null>>
  draggedOverElementPosition: "top" | "bottom" | "middle" | null
  setDraggedOverElementPosition: Dispatch<SetStateAction<"top" | "bottom" | "middle" | null>>
  draggedDescendantIds: string[]
  setDraggedDescendantIds: Dispatch<SetStateAction<string[]>>
  dragMode: "drag" | "drop" | null
  setDragMode: Dispatch<SetStateAction<"drag" | "drop" | null>>
  draggedElement: TElement | null
  setDraggedElement: Dispatch<SetStateAction<TElement | null>>
  isElementInteractionDisabled?: boolean
  badge?: TBadge
  contentType: "mainContent" | "backgroundContent" | "cardContent" | null
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const isNavbar = useMemo(() => element?.id === "Navbar", [element?.id])
  const isPurchaseDrawer = useMemo(() => element?.id === "purchase drawer", [element?.id])
  const isFooter = useMemo(() => element?.id === "Footer", [element?.id])
  const isMarqueeItem = useMemo(() => element?.name === "marqueeItem", [element?.name])

  const isInteractionDisabled =
    isElementInteractionDisabled || isNavbar || isPurchaseDrawer || isFooter || isMarqueeItem

  const hasClickedOutsideElement = useClickedOutsideRef(containerRef)
  const { parsedJson, setJson } = useTemplateJsonContext()
  const { selectedElementId, setSelectedElementId } = useElementContext()
  const { selectedSubstitutionKey, setSelectedSubstitutionKey } = useSpecificPaywallContext({
    selectedSubstitutionKey: true,
    setSelectedSubstitutionKey: true,
  })
  const formState = useFormState()
  const { values } = formState || {}
  const { substitutions } = values || {}
  const {
    input: { onChange: onChangeSubstitutions },
  } = useField("substitutions")

  const isButtonBase = useMemo(() => element?.name === "buttonBase", [element?.name])
  const isImageBase = useMemo(() => element?.name === "imageBase", [element?.name])
  const isHeading = useMemo(() => element?.name === "heading", [element?.name])
  const isSubheading = useMemo(() => element?.name === "subheading", [element?.name])

  const showSettingsIcon = !isButtonBase && !isHeading && !isSubheading

  // Matches this element to its corresponding substitution key in substitutions array
  const thisSubstitution = useMemo(
    () =>
      _.find(substitutions, (sub) => sub.key.toLowerCase().includes(element.id.toLowerCase())) ||
      {},
    [substitutions, element.id]
  )

  const elementName = useMemo(() => {
    if (element.customName) return element.customName
    return _.startCase(
      isButtonBase || isImageBase
        ? element.name.replace("Base", "")
        : element.name === "listV2"
          ? "list"
          : element.name
    )
  }, [element.customName, element.name, isButtonBase, isImageBase])

  // If an element is clicked in the preview that matches this element then set the
  // selectedElement to be this one.
  useEffect(() => {
    if (
      (selectedSubstitutionKey
        ?.split(" ")
        .filter((s) => s.startsWith("__sw-"))
        .pop() === element.id.toLowerCase() &&
        selectedElementId !== element.id) ||
      selectedSubstitutionKey?.toLowerCase() === element?.id?.toLowerCase()
    ) {
      setSelectedElementId(element.id)
    }
  }, [selectedSubstitutionKey, selectedElementId, setSelectedElementId, element.id])

  const {
    input: { onChange: onChangeJson },
  } = useField("templateJson")

  const [isChildrenOpen, setIsChildrenOpen] = useState(true)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [cursorPosition, setCursorPosition] = useState<"top" | "bottom" | null>(null)
  const [dragPosition, setDragPosition] = useState<"top" | "middle" | "bottom" | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isSelected, setIsSelected] = useState(false)

  const handlePreviewElementClick = (e) => {
    const key = e.detail?.key || null

    if (!isSelected && key === thisSubstitution.key) {
      setIsSelected(true)
    } else if (isSelected && key !== thisSubstitution.key) {
      setIsSelected(false)
    }
  }

  useEffect(() => {
    // Event listener when user clicks an element in the preview.
    document.addEventListener(EElementEvent.CLICK_PREVIEW_ELEMENT, handlePreviewElementClick)

    return () => {
      document.removeEventListener(EElementEvent.CLICK_PREVIEW_ELEMENT, handlePreviewElementClick)
    }
  })

  const handleNavigatorElementClick = (e) => {
    const key = e.detail?.key || null

    if (!isSelected && key === thisSubstitution.key) {
      setIsSelected(true)
    } else if (isSelected && key !== thisSubstitution.key) {
      setIsSelected(false)
    }
  }

  useEffect(() => {
    // Event listener when user clicks an element in the navigator.
    document.addEventListener(EElementEvent.CLICK_NAVIGATOR_ELEMENT, handleNavigatorElementClick)

    return () => {
      document.removeEventListener(
        EElementEvent.CLICK_NAVIGATOR_ELEMENT,
        handleNavigatorElementClick
      )
    }
  })

  const marginLeft = `${level * 12}px`

  const hasChildren = element?.children && element.children.length > 0

  const isChildrenAllowed = useMemo(
    () => elementsWithChildren.includes(element.name),
    [element.name]
  )

  const isDescendantOfDraggedElement = useMemo(
    () => (draggedDescendantIds || []).includes(element.id),
    [draggedDescendantIds, element.id]
  )

  const isDraggingOver = useMemo(
    () => draggedOverElementId === element.id,
    [draggedOverElementId, element.id]
  )

  const isElementDisabled = useMemo(() => {
    return dragMode === "drag" && isDescendantOfDraggedElement
  }, [dragMode, isDescendantOfDraggedElement])

  const onClickOpenClose = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      setIsChildrenOpen(!isChildrenOpen)
    },
    [isChildrenOpen]
  )

  const onAddComponent = useCallback(
    ({
      componentName,
      marqueeItemCount,
      newSiblingPlacement,
    }: {
      componentName: string
      marqueeItemCount: TMarqueeItemCount
      newSiblingPlacement?: "before" | "after" | null
    }) => {
      const newElement = createComponent(
        componentName,
        {
          marqueeItemCount,
        },
        parsedJson?.pageSettings
      )

      const { json: newJson, parsedJson: newParsedJson } = addSibling({
        parsedJson,
        elementId: element.id,
        newElement,
        placement: newSiblingPlacement!,
      })

      updateNecessarySubstitutions(newElement, substitutions, onChangeSubstitutions)

      setJson(newJson)
      onChangeJson(newParsedJson)
      document.dispatchEvent(new CustomEvent(ESelectComponentModalEvent.CLOSE))

      setSelectedElementId(newElement?.id)
    },
    [
      element,
      onChangeJson,
      parsedJson,
      setJson,
      setSelectedElementId,
      substitutions,
      onChangeSubstitutions,
    ]
  )

  const onDuplicateElement = useCallback(() => {
    const result = duplicateElement({
      parsedJson,
      element: element!,
    })
    const newJson = result?.json
    const newElement = result?.newElement
    const newElementId = result?.newElementId
    const newParsedJson = result?.parsedJson

    // Adding ts-ignore because not all elements will have children, but I don't want to
    // write out the conditions for each element type so whatever.
    const flattenedElements = [element, ...flattenArray(element?.children || [])]
    const newFlattenedElements = [newElement, ...flattenArray(newElement?.children || [])]

    const oldNewIdPairs = _.map(flattenedElements, (el, i) => {
      return { oldId: el.id, newId: newFlattenedElements[i].id }
    })

    if (newJson && newElementId && newParsedJson) {
      setJson(newJson)
      onChangeJson(newParsedJson)
      setSelectedElementId(newElementId)

      /**
       * This will find all the substutions that are related to the duplicated elements
       * and duplicate them with the new keys based of the ids of the new elements.
       * This will also handle nested elements that have substitution keys like:
       * Card __sw-123 > Title __sw-456, and correctly update the component ids in the
       * entire substitution key.
       */
      const duplicatedSubstitutions = flattenedElements.map((flatEl, i) => {
        const oldSubs = substitutions.filter((sub) => {
          const subId = sub.key
            .split(" ")
            .filter((x) => x.startsWith("__sw-"))
            .pop()
          return subId === flatEl.id.toLowerCase()
        })
        return _.map(oldSubs, (sub) => {
          let subKey = sub.key
          oldNewIdPairs.forEach((pair) => {
            subKey = subKey.replaceAll(pair.oldId.toLowerCase(), pair.newId.toLowerCase())
          })
          return { ...sub, key: subKey }
        })
      })

      onChangeSubstitutions([...substitutions, ..._.flatten(duplicatedSubstitutions)])
    }
  }, [
    element,
    onChangeJson,
    onChangeSubstitutions,
    parsedJson,
    setJson,
    setSelectedElementId,
    substitutions,
  ])

  const onDeleteElement = useCallback(() => {
    if (!element.id) return

    const { json: newJson, parsedJson: newParsedJson } =
      deleteElement({
        parsedJson,
        elementId: element.id,
      }) || {}

    if (newJson) {
      setJson(newJson)
      onChangeJson(newParsedJson)
      setSelectedElementId(null)
      setSelectedSubstitutionKey(undefined)
    }
  }, [element, onChangeJson, parsedJson, setJson, setSelectedElementId, setSelectedSubstitutionKey])

  const onCloseDeleteModal = () => {
    setIsDeleteModalOpen(false)
  }

  const onAddChildElement = useCallback(
    ({
      componentName,
      marqueeItemCount,
    }: {
      componentName: string
      marqueeItemCount: TMarqueeItemCount
    }) => {
      if (!element.id || !componentName) return

      const newElement = createComponent(
        componentName,
        {
          marqueeItemCount,
        },
        parsedJson?.pageSettings
      )

      const { json: newJson, parsedJson: newParsedJson } = addChildToElement({
        parsedJson,
        elementId: element.id,
        newElement,
      })

      updateNecessarySubstitutions(newElement, substitutions, onChangeSubstitutions)

      setJson(newJson)
      onChangeJson(newParsedJson)
      document.dispatchEvent(new CustomEvent(ESelectComponentModalEvent.CLOSE))
    },
    [element, onChangeJson, parsedJson, setJson, substitutions, onChangeSubstitutions]
  )

  const getCursorPosition = useCallback(
    (e: MouseEvent) => {
      const containerBounds = containerRef.current?.getBoundingClientRect()
      const y = e.clientY - containerBounds!.top
      if (y < 25) {
        setCursorPosition("top")
      } else if (y > 30) {
        setCursorPosition("bottom")
      } else {
        setCursorPosition(null)
      }
    },
    [containerRef, setCursorPosition]
  )

  const onDragStart = useCallback(
    (e: MouseEvent) => {
      /**
       * We don't want to allow dragging of marquee items because
       */
      if (isInteractionDisabled) return

      // @ts-ignore
      e.dataTransfer.effectAllowed = "move"

      setIsDragging(true)
      setDragMode("drag")
      setDraggedElement(element)

      const flattened = flattenArray(element.children || [])
      const descendantIds = _.map(flattened, (el) => el.id)

      setDraggedDescendantIds(descendantIds)
    },
    [
      setIsDragging,
      element,
      setDraggedDescendantIds,
      setDragMode,
      setDraggedElement,
      isInteractionDisabled,
    ]
  )

  const endDragging = useCallback(() => {
    setIsDragging(false)
    setDraggedOverElementId(null)
    setDragMode(null)
    setDraggedElement(null)
  }, [setIsDragging, setDraggedOverElementId, setDragMode, setDraggedElement])

  const onDrop = useCallback(() => {
    if (isDescendantOfDraggedElement) return

    if (draggedOverElementId === draggedElement?.id) return

    if (!draggedOverElementId) return

    const deleteResult = deleteElement({ parsedJson, elementId: draggedElement?.id! })
    const deletedParsedJson = deleteResult?.parsedJson

    if (draggedOverElementPosition === "middle" && isChildrenAllowed) {
      const addResult = addChildToElement({
        parsedJson: deletedParsedJson!,
        elementId: draggedOverElementId!,
        newElement: draggedElement!,
      })
      setJson(addResult.json)
    } else if (draggedOverElementPosition === "top") {
      const addResult = addSibling({
        parsedJson: deletedParsedJson!,
        elementId: draggedOverElementId!,
        newElement: draggedElement!,
        placement: "before",
      })
      setJson(addResult.json)
    } else if (draggedOverElementPosition === "bottom") {
      const addResult = addSibling({
        parsedJson: deletedParsedJson!,
        elementId: draggedOverElementId!,
        newElement: draggedElement!,
        placement: "after",
      })
      setJson(addResult.json)
    }
    endDragging()
  }, [
    isChildrenAllowed,
    draggedOverElementPosition,
    endDragging,
    isDescendantOfDraggedElement,
    draggedOverElementId,
    draggedElement,
    parsedJson,
    setJson,
  ])

  const onDragEnd = useCallback(() => {
    endDragging()
  }, [endDragging])

  const [dragDebounce, setDragDebounce] = useState(0)

  const onDragOver = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()
      if (isInteractionDisabled) return
      if (isDragging === true) return
      if (dragDebounce < 4) {
        setDragDebounce(dragDebounce + 1)
        return
      }

      const containerBounds = containerRef.current?.getBoundingClientRect()
      const y = event.clientY - containerBounds!.top
      const containerHeight = containerBounds!.height
      const containerMiddle = containerHeight / 2

      if (y < containerMiddle - 9) {
        setDragPosition("top")
      } else if (y > containerMiddle + 17) {
        setDragPosition("bottom")
      } else {
        setDragPosition("middle")
      }

      const target = event.target as HTMLElement

      // Checks if dragging over this element
      const targetId = target.getAttribute("data-drag-id")
      const parentId = target.parentElement?.getAttribute("data-drag-id")
      const elementId = targetId || parentId
      if (elementId === element.id) {
        setDraggedOverElementId(element.id)
      }

      setDragDebounce(0)
    },
    [
      dragDebounce,
      element,
      isDragging,
      setDraggedOverElementId,
      setDragPosition,
      containerRef,
      isInteractionDisabled,
    ]
  )

  const nameBgColor = useMemo(() => {
    if (isElementDisabled || !isDraggingOver || dragPosition !== "middle") return ""

    if (isChildrenAllowed) {
      return "bg-accent-light"
    } else {
      return "bg-alert-background"
    }
  }, [isElementDisabled, isChildrenAllowed, isDraggingOver, dragPosition])

  const [showNameInput, setShowNameInput] = useState(false)
  const onNameDoubleClick = () => {
    setNameInputValue(elementName)
    setShowNameInput(true)
  }
  const [nameInputValue, setNameInputValue] = useState(elementName)

  const onChangeNameInput = useCallback((e) => {
    setNameInputValue(e.target.value)
  }, [])

  const onKeyDownNameInput = useCallback(
    (e) => {
      if (e.key === "Escape") {
        setShowNameInput(false)
        setNameInputValue(elementName)
      } else if (e.key === "Enter") {
        const newElement = {
          ...element,
          customName: nameInputValue,
        }
        const { json: newJson } = updateJsonByElement(parsedJson, newElement)
        setJson(newJson)
        setShowNameInput(false)
      }
    },
    [elementName, nameInputValue, parsedJson, setJson, element]
  )

  useEffect(() => {
    if (isDraggingOver) {
      if (dragPosition === "middle" && !isChildrenAllowed) {
        setDraggedOverElementPosition(null)
      } else {
        setDraggedOverElementPosition(dragPosition)
      }
    }
  }, [isDraggingOver, dragPosition, setDraggedOverElementPosition, isChildrenAllowed])

  useEffect(() => {
    if (hasClickedOutsideElement && showNameInput) {
      setShowNameInput(false)
    }
  }, [hasClickedOutsideElement, showNameInput])

  if (!element) return null

  return (
    <>
      <Container
        ref={containerRef}
        onMouseMove={getCursorPosition}
        draggable={!isInteractionDisabled ? "true" : "false"}
        data-drag-id={element.id}
        data-drag-element="container"
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={isElementDisabled ? "opacity-50" : ""}
      >
        {!isElementDisabled && isDraggingOver && ["top", "bottom"].includes(dragPosition || "") && (
          <DraggedEdgeIndicator
            className={dragPosition === "top" ? "top-[-1px]" : "bottom-0"}
            style={{ left: marginLeft }}
          />
        )}
        <SFlex
          width="w-full"
          align="center"
          gap="3"
          data-drag-id={element.id}
          data-drag-element="flex"
        >
          {showNameInput ? (
            <SInput
              autoFocus
              noMargin
              type="text"
              value={nameInputValue}
              onChange={onChangeNameInput}
              onKeyDown={onKeyDownNameInput}
              fontSize="sm"
              height="h-[42px]"
              padding="pl-4"
              containerStyle={{ marginLeft }}
            />
          ) : (
            <Name
              className={`${isSelected ? SelectedElementStyle : ""} ${nameBgColor}`}
              style={{ marginLeft }}
              onClick={() => {
                setSelectedElementId(element.id)
                !!thisSubstitution?.key && setSelectedSubstitutionKey(thisSubstitution.key)
                document.dispatchEvent(
                  new CustomEvent(EElementEvent.CLICK_NAVIGATOR_ELEMENT, {
                    detail: {
                      key: thisSubstitution?.key,
                    },
                  }) as TClickNavigatorEvent
                )
              }}
              onDoubleClick={onNameDoubleClick}
              data-drag-id={element.id}
              data-drag-element="name"
              data-substitution-key={thisSubstitution?.key}
            >
              <div>
                {hasChildren && (
                  <div
                    className={`${OpenCloseIconContainer} group-hover:left-[-25px]`}
                    onClick={onClickOpenClose}
                  >
                    <Icon name={isChildrenOpen ? "MdExpandMore" : "MdChevronRight"} size="sm" />
                  </div>
                )}
                <span className="flex items-center gap-2 ml-2">
                  {elementName}
                  {isInteractionDisabled && <Icon name="MdLock" size="sm" color="whiteOpacity80" />}
                </span>
              </div>
              {badge && (
                <Badge>
                  {badge.tooltip ? (
                    <STooltip content={badge.tooltip} width="280px" placement="top-end">
                      {badge.text}
                    </STooltip>
                  ) : (
                    <>{badge.text}</>
                  )}
                </Badge>
              )}
            </Name>
          )}

          {!isElementInteractionDisabled && (
            <SFlex gap="2">
              {showSettingsIcon && (
                <SideIconContainer
                  onClick={() => {
                    setSelectedElementId(element.id)
                    toggleElementDetails(true)
                    setSelectedElementId(element.id)
                    !!thisSubstitution?.key && setSelectedSubstitutionKey(thisSubstitution.key)
                  }}
                >
                  <STooltip content="View element settings" placement="bottom-start" delay={200}>
                    <Icon name="SwGear" />
                  </STooltip>
                </SideIconContainer>
              )}
              {showSettingsIcon &&
                !isNavbar &&
                !isPurchaseDrawer &&
                !isFooter &&
                !isInteractionDisabled && <SDivider orientation="vertical" />}
              {elementsWithChildren.includes(element.name) && (
                <SideIconContainer
                  onClick={() => {
                    document.dispatchEvent(
                      new CustomEvent(ESelectComponentModalEvent.OPEN, {
                        detail: {
                          title: "Select Child Component",
                          onAddComponent: onAddChildElement,
                          newSiblingPlacement: "after",
                          contentType,
                        },
                      }) as TOpenSelectComponentModalEvent
                    )
                  }}
                >
                  <STooltip content="Add child element" placement="bottom-start" delay={200}>
                    <Icon name="MdSubdirectoryArrowLeft" size="sm" />
                  </STooltip>
                </SideIconContainer>
              )}
              {!isInteractionDisabled && (
                <SideIconContainer onClick={onDuplicateElement}>
                  <STooltip content="Duplicate this element" placement="bottom-start" delay={200}>
                    <Icon name="MdContentCopy" size="sm" />
                  </STooltip>
                </SideIconContainer>
              )}
              {!isInteractionDisabled && (
                <SideIconContainer onClick={() => setIsDeleteModalOpen(true)}>
                  <Icon name="IoTrashOutline" size="sm" />
                </SideIconContainer>
              )}
            </SFlex>
          )}
        </SFlex>
        {!showNameInput && !isInteractionDisabled && (
          <>
            {cursorPosition === "top" && (
              <AddSiblingIcon
                onClick={() => {
                  document.dispatchEvent(
                    new CustomEvent(ESelectComponentModalEvent.OPEN, {
                      detail: {
                        title: "Select Component",
                        onAddComponent: onAddComponent,
                        newSiblingPlacement: "before",
                        contentType,
                      },
                    }) as TOpenSelectComponentModalEvent
                  )
                }}
                className="top-[-1px]"
              >
                <div className="bg-grayscale-darkest">
                  <Icon name="MdAddBox" size="lg" color="accent" />
                </div>
              </AddSiblingIcon>
            )}
            {cursorPosition === "bottom" && (
              <AddSiblingIcon
                onClick={() => {
                  document.dispatchEvent(
                    new CustomEvent(ESelectComponentModalEvent.OPEN, {
                      detail: {
                        title: "Select Component",
                        onAddComponent: onAddComponent,
                        newSiblingPlacement: "after",
                        contentType,
                      },
                    }) as TOpenSelectComponentModalEvent
                  )
                }}
                className="bottom-[-1px]"
              >
                <div className="bg-grayscale-darkest">
                  <Icon name="MdAddBox" size="lg" color="accent" />
                </div>
              </AddSiblingIcon>
            )}
          </>
        )}
      </Container>

      {hasChildren &&
        isChildrenOpen &&
        (element?.children || []).map((child, index) => {
          let badge
          let disableInteractions = false

          if (element.name === "modal" && index === 0) {
            badge = {
              text: "open modal",
              tooltip:
                "This element will open the contents of its sibling element inside a modal. It cannot be moved or deleted. If you want to use a different modal trigger element you must create a new modal and specify a different trigger element.",
            }
            disableInteractions = true
          }
          return (
            <Element
              key={`child-${child.id}`}
              element={child}
              level={level + 1}
              toggleElementDetails={toggleElementDetails}
              draggedOverElementId={draggedOverElementId}
              setDraggedOverElementId={setDraggedOverElementId}
              draggedOverElementPosition={draggedOverElementPosition}
              setDraggedOverElementPosition={setDraggedOverElementPosition}
              draggedDescendantIds={draggedDescendantIds}
              setDraggedDescendantIds={setDraggedDescendantIds}
              dragMode={dragMode}
              setDragMode={setDragMode}
              draggedElement={draggedElement}
              setDraggedElement={setDraggedElement}
              badge={badge}
              isElementInteractionDisabled={disableInteractions}
              contentType={contentType}
            />
          )
        })}

      <SModal isOpen={isDeleteModalOpen} onClose={onCloseDeleteModal}>
        <SModal.Content onClose={onCloseDeleteModal}>
          <SModal.Title>Delete Component?</SModal.Title>
          <SFlex gap="3">
            <SButton onClick={() => onCloseDeleteModal()} className="mt-2 w-full">
              Cancel
            </SButton>
            <SButton
              onClick={() => {
                onDeleteElement()
                onCloseDeleteModal()
              }}
              className="mt-2 w-full"
              variant="secondary"
            >
              Delete
            </SButton>
          </SFlex>
        </SModal.Content>
      </SModal>
    </>
  )
}

export default Element
